import { useState, useEffect, useRef, Fragment } from "react";
import { Plus, PackageSearch, Search, X, Loader2, ChevronLeft, ChevronRight, CopyPlus, ListPlus } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { computePqPrice, pqNum } from "@/lib/pqPrice";
import { OrderProductAttachPanel, toProcurementCarton } from "@/modules/order-products";
import { toast } from "@/components/ui/sonner";
import { pqChanges } from "@/lib/pqSync";
import { PqSyncStatus } from "@/components/procurement/PqSyncStatus";
import { OtherCostDetailsDialog, sumOtherDetails } from "@/components/OtherCostDetailsDialog";
import { TEMPLATE_FIELDS, computeFromFormula, validateFormula } from "@/lib/pqTemplate";

const EMPTY = {
  item_number: "", name_cn: "", size: "", factory: "", price: "",
  quote_customer: "",
  quote_date: new Date().toISOString().slice(0, 10),
  thickness: "", remark: "", others: "", salesperson: "",
  profit_tax: "", processing_fee: "", brand_paper: "", plastic_bag: "",
  price_per_meter: "", material_size: "", carton_freight: "", liner_board: "",
  packing_rate: "", qty_hq: "", width: "", length: "", height: "", gross_weight: "",
  tag: "", others_details: [],
};

// 2026-06 · Layout plan A — three semantic sections, compact boxes.
//   基本信息 (identity) · 价格信息 (cost build-up) · 装箱数据 (carton data)
// 价格 is READ-ONLY: it is always the formula result (user spec).
const REQUIRED = ["item_number", "name_cn", "size", "factory"];
const BASIC_FIELDS = [
  ["item_number", 1], ["name_cn", 2], ["quote_customer", 1],
  ["quote_date", 1], ["salesperson", 1], ["factory", 1], ["tag", 1],
  ["size", 2], ["thickness", 2],
];
// Money inputs feeding the price formula.
const COST_FIELDS = ["price_per_meter", "material_size", "processing_fee", "brand_paper",
                     "plastic_bag", "liner_board", "carton_freight"];
const CARTON_FIELDS = ["packing_rate", "length", "width", "height", "qty_hq"];
const CM_FIELDS = new Set(["length", "width", "height"]);

const NUMERIC = new Set(["price", "price_per_meter", "qty_hq", "width", "length", "height", "gross_weight"]);

// 2026-06-28 · 表单 → 接口 payload（手动保存与自动保存共用）。
const buildPqPayload = (form) => {
  const payload = {};
  for (const [k, v] of Object.entries(form)) {
    payload[k] = NUMERIC.has(k) ? Number(v || 0) : v;
  }
  payload.price = computePqPrice(form);   // 价格 is always the formula result
  return payload;
};

export function NewProcurementQuoteDialog({ open, onOpenChange, onCreated, initialValue = null, createUrl = null, onPrev = null, onNext = null, navInfo = null, template = null, categoryDefaults = null, onClearTemplate = null }) {  // 2026-06 · 回车跳到下一个字段（多行备注里回车仍然换行）
  const dialogRef = useRef(null);
  const { t } = useLanguage();
  const { user } = useAuth();
  // 2026-06 · ``copiedId`` keeps the dialog open on the record produced by
  // 复制为新记录 so several sizes of the same product can be entered in a row.
  const [copiedId, setCopiedId] = useState(null);
  const editingId = copiedId || initialValue?.id || null;
  const isEditing = Boolean(editingId);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [otherDetailsOpen, setOtherDetailsOpen] = useState(false);
  const dirtyRef = useRef(false);
  const sessionRef = useRef(null);
  const requestIdRef = useRef(null);
  const [autoState, setAutoState] = useState("");
  const [syncRecord, setSyncRecord] = useState(initialValue);

  const persistDraft = async (session = sessionRef.current) => {
    if (!session?.id || !session.touched) return;
    if (session.saving) {
      await session.saving;
      return persistDraft(session);
    }
    const snapshot = session.draft;
    const payload = pqChanges(snapshot, session.base);
    if (!payload) return;
    if (REQUIRED.some((k) => String(snapshot[k] ?? "").trim() === "")) {
      throw new Error(t("pq.required"));
    }
    if (sessionRef.current === session) setAutoState("saving");
    session.saving = (async () => {
      try {
        const { data } = await api.put(`/procurement-quotes/${session.id}`, payload);
        session.base = snapshot;
        if (sessionRef.current === session) {
          setSyncRecord(data);
          dirtyRef.current = Boolean(pqChanges(session.draft, session.base));
          setAutoState(dirtyRef.current ? "" : "saved");
        }
        onCreated?.({ keepOpen: true });
      } catch (error) {
        if (sessionRef.current === session) { dirtyRef.current = true; setAutoState("error"); }
        throw error;
      } finally { session.saving = null; }
    })();
    await session.saving;
    if (pqChanges(session.draft, session.base)) return persistDraft(session);
  };

  useEffect(() => {
    if (!open) return;
    const hydrated = { ...EMPTY, salesperson: user?.name || "" };
    // 2026-09-30 · 三层叠加：模版默认 → 种类默认覆盖 → initialValue 覆盖 (用户手改)
    if (template?.fields) {
      for (const k of TEMPLATE_FIELDS) {
        const def = template.fields[k]?.default;
        if (def !== undefined && def !== null && def !== "") hydrated[k] = def;
      }
    }
    if (categoryDefaults) {
      for (const [k, v] of Object.entries(categoryDefaults)) {
        if (v !== undefined && v !== null && v !== "") hydrated[k] = v;
      }
    }
    if (initialValue) for (const k of Object.keys(EMPTY)) {
      if (initialValue[k] !== undefined && initialValue[k] !== null) hydrated[k] = initialValue[k];
    }
    const session = { id: initialValue?.id || null, base: hydrated, draft: hydrated, saving: null };
    sessionRef.current = session;
    requestIdRef.current = crypto.randomUUID();
    setForm(hydrated);
    setCopiedId(null);
    setSyncRecord(initialValue);
    dirtyRef.current = false;
    setAutoState("");
    return () => {
      // This effect's record may have been copied; flush that copy as well.
      const copy = sessionRef.current;
      persistDraft(session).catch((error) => toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message));
      if (copy && copy !== session) {
        persistDraft(copy).catch((error) => toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message));
      }
    };
  }, [open, initialValue?.id, user?.name, template?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (sessionRef.current?.id === editingId) sessionRef.current.draft = form;

  // 2026-06 · QTY HQ auto-computes from carton dims (cm) × 装箱率 the moment
  // any of them changes — same formula as 40尺柜装箱量:
  //   65,000,000 ÷ 长 ÷ 宽 ÷ 高 × 装箱率
  useEffect(() => {
    const L = pqNum(form.length), W = pqNum(form.width), H = pqNum(form.height);
    const rate = pqNum(form.packing_rate);
    if (L > 0 && W > 0 && H > 0 && rate > 0) {
      const qty = Math.round(65_000_000 / L / W / H * rate);
      setForm((f) => (String(f.qty_hq) === String(qty) ? f : { ...f, qty_hq: qty }));
    }
  }, [form.length, form.width, form.height, form.packing_rate]);

  // 2026-06 · 其他 always mirrors the 其他明细 sum so 价格 stays consistent.
  useEffect(() => {
    const rows = form.others_details || [];
    if (rows.length === 0) return;
    const total = String(sumOtherDetails(rows));
    setForm((f) => (String(f.others) === total ? f : { ...f, others: total }));
  }, [form.others_details]);

  useEffect(() => {
    if (!open || !editingId || !dirtyRef.current) return;
    const timer = setTimeout(() => {
      persistDraft().catch((error) => toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message));
    }, 900);
    return () => clearTimeout(timer);
  }, [form, open, editingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const warn = (event) => {
      if (dirtyRef.current || sessionRef.current?.saving) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  const leaveAfterSave = async (action) => {
    try { await persistDraft(); action?.(); }
    catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message); }
  };

  const canOpen = user && (user.role === "admin" || user.role === "buyer" || user.role === "procurement_manager");
  if (!canOpen) return null;

  // 2026-09-30 · hidden-field helper + template-aware price. If a template is
  // attached we use its formula (hidden fields are treated as 0); otherwise
  // we fall back to the standard _compute_pq_price.
  const isFieldVisible = (k) => {
    if (!template?.fields) return true;
    const cfg = template.fields[k];
    return cfg ? cfg.visible !== false : true;
  };
  const templatePrice = (f) => {
    try {
      validateFormula(template.formula, TEMPLATE_FIELDS);
      const vals = Object.fromEntries(TEMPLATE_FIELDS.map((k) => [k, isFieldVisible(k) ? f[k] : 0]));
      return computeFromFormula(template.formula, vals);
    } catch (_) {
      return computePqPrice(f);
    }
  };
  const computedPriceLive = template ? templatePrice(form) : computePqPrice(form);

  const setField = (k, v) => {
    dirtyRef.current = true;
    if (sessionRef.current) sessionRef.current.touched = true;
    setAutoState("");
    setForm((f) => ({ ...f, [k]: v }));
  };

  // 2026-06 · Pull carton data from a 合同订单产品 record into the form.
  const applyOrderPacking = (row) => {
    if (!row) return;
    const carton = toProcurementCarton(row);
    dirtyRef.current = true;
    if (sessionRef.current) sessionRef.current.touched = true;
    setForm((f) => ({
      ...f,
      ...Object.fromEntries(Object.entries(carton).filter(([, v]) => v !== "" && v !== 0)),
    }));
  };

  // Fetch the picked product's details and hydrate item_number / name_cn / size.
  // Uses description_zh when available (采购报价 is a Chinese-facing entity).
  const handleProductPick = async (product) => {
    setPickerOpen(false);
    setField("item_number", product.item_number);
    const nameCn = product.description_zh || product.description || "";
    setField("name_cn", nameCn);
    // pull the first price list size if the product has any
    try {
      const { data } = await api.get(`/products/${encodeURIComponent(product.item_number)}`);
      const firstSize = data.price_lists?.[0]?.size || "";
      if (firstSize) setField("size", firstSize);
    } catch (err) {
      // Silent — item + name are already filled, size is optional to auto-fill.
      // Log so devs can spot a broken /products/{item} endpoint via devtools.
      console.debug("[pq picker] size auto-fill failed:", err);
    }
    toast.success(t("pq.pickFilled"));
  };

  // 2026-06 · ``asCopy`` saves the current form as a NEW record (复制) even in
  // edit mode — handy for quoting the same product with tweaked costs.
  const handleSubmit = async (asCopy = false) => {
    // client-side required check
    for (const key of REQUIRED) {
      if (String(form[key] ?? "").trim() === "") {
        toast.error(`${t(`pq.${camelKey(key)}`)} — ${t("pq.required")}`);
        return;
      }
    }
    if (saving) return;
    setSaving(true);
    try {
      if (isEditing) await persistDraft();
      const payload = { ...buildPqPayload(form), request_id: requestIdRef.current };
      if (template?.id) {
        payload.template_id = template.id;
        payload.price = templatePrice(form);
      }
      if (asCopy) {
        payload.quote_date = new Date().toISOString().slice(0, 10);
        payload.salesperson = user?.name || "";
      }
      if (isEditing && !asCopy) {
        toast.success(t("pq.savedApp"));
      } else {
        const { data } = await api.post(createUrl || "/procurement-quotes", payload);
        toast.success(asCopy ? t("pq.copiedOk") : t("pq.createdOk"));
        requestIdRef.current = crypto.randomUUID();
        if (asCopy) {
          const next = { ...form, quote_date: payload.quote_date, salesperson: payload.salesperson };
          sessionRef.current = { id: data.id, base: next, draft: next, saving: null };
          dirtyRef.current = false;
          setSyncRecord(data);
          setForm(next);
          setCopiedId(data.id);
          onCreated?.({ keepOpen: true });
          return;
        }
      }
      dirtyRef.current = false;
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      setAutoState("error");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const inputProps = (k) => ({
    value: form[k] ?? "",
    onChange: (e) => setField(k, e.target.value),
    "data-testid": `new-pq-${k}-input`,
    className: "h-8 text-sm",
  });

  const computedPrice = computedPriceLive;
  const hasOtherDetails = (form.others_details || []).length > 0;


  // 注意：这是渲染函数而不是组件 —— 在父组件内部定义组件会让 React 每次
  // 重新渲染都换掉输入框节点，导致输入一个字符就失去焦点。
  const handleEnterNav = (e) => {
    if (e.key !== "Enter" || e.shiftKey || e.target.tagName !== "INPUT") return;
    e.preventDefault();
    const inputs = Array.from(
      dialogRef.current?.querySelectorAll(
        "input:not([type=hidden]):not([disabled]):not([readonly])") || [],
    ).filter((el) => el.offsetParent !== null);
    const next = inputs[inputs.indexOf(e.target) + 1];
    if (next) { next.focus(); next.select?.(); }
  };

  const field = ({ k, span = 1, type, suffix, hint }) => (
    <div className={`space-y-1 ${span === 2 ? "col-span-2" : ""}`}>
      <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
        {t(`pq.${camelKey(k)}`)}
        {REQUIRED.includes(k) && <span className="text-red-500">*</span>}
        {hint && <span className="text-[9px] text-brand">{hint}</span>}
      </Label>
      <div className="relative">
        <Input type={type} step={type === "number" ? "0.01" : undefined} {...inputProps(k)}
          className={`h-8 text-sm ${suffix ? "pr-7" : ""}`} />
        {suffix && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );

  const sectionTitle = (children) => (
    <div className="flex items-center gap-2 mt-1">
      <span className="h-3.5 w-0.5 bg-brand rounded-full" aria-hidden />
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{children}</h3>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => o ? onOpenChange(true) : leaveAfterSave(() => onOpenChange(false))}>
      <DialogContent ref={dialogRef} onKeyDown={handleEnterNav}
        className="max-w-[960px] max-h-[96vh] overflow-y-auto" data-testid="new-pq-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={16} className="text-brand" /> {isEditing ? t("pq.editTitle") : t("pq.newTitle")}
            {/* 2026-06 · Record navigation (edit mode only). */}
            {isEditing && (onPrev || onNext) && (
              <span className="ml-auto flex items-center gap-1 text-xs font-normal">
                <Button type="button" variant="outline" size="sm" className="h-7 px-2"
                  onClick={() => leaveAfterSave(onPrev)} disabled={!onPrev || saving} data-testid="pq-edit-prev">
                  <ChevronLeft size={14} /> {t("common.prev")}
                </Button>
                {navInfo && (
                  <span className="font-mono text-muted-foreground px-1" data-testid="pq-edit-nav-info">
                    {navInfo.index + 1}/{navInfo.total}
                  </span>
                )}
                <Button type="button" variant="outline" size="sm" className="h-7 px-2"
                  onClick={() => leaveAfterSave(onNext)} disabled={!onNext || saving} data-testid="pq-edit-next">
                  {t("common.next")} <ChevronRight size={14} />
                </Button>
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* quick-fill from Products library — only in create mode */}
        {!isEditing && (
          <>
            <div className="flex items-center justify-between gap-2 -mt-1">
              <p className="text-xs text-muted-foreground">{t("pq.pickHint")}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                data-testid="new-pq-pick-product-button"
                className="h-8 rounded-md border-brand/40 text-brand hover:bg-brand/5"
              >
                <PackageSearch size={14} /> {t("pq.pickFromProducts")}
              </Button>
            </div>
            <ProductPickerDialog
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              onPick={handleProductPick}
            />
          </>
        )}

        {/* 2026-09-30 · 当前模版面包屑 — 让采购员一眼看到当前套用的模版 */}
        {template && (
          <div className="flex items-center gap-2 -mt-1 flex-wrap" data-testid="new-pq-template-breadcrumb">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand/10 text-brand font-semibold">
              模版：{template.name || "(未命名)"}
            </span>
            {template.categories?.length > 0 && (
              <span className="text-[11px] text-muted-foreground">适用：{template.categories.join("、")}</span>
            )}
            {onClearTemplate && !isEditing && (
              <button type="button" className="text-[11px] text-muted-foreground hover:text-brand underline"
                onClick={onClearTemplate} data-testid="new-pq-template-clear">
                换模版
              </button>
            )}
          </div>
        )}

        {/* 2026-06 · Centered badge so it's obvious we're now on the copy. */}
        {copiedId && (
          <div className="flex justify-center -mt-1">
            <span className="px-3 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-xs font-semibold"
              data-testid="new-pq-copied-badge">
              {t("pq.newRecordBadge")}
            </span>
          </div>
        )}

        {/* ── 基本信息 ────────────────────────────────────────────────── */}
        {sectionTitle(t("pq.sectionBasic"))}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1.5">
          {BASIC_FIELDS.map(([k, span]) => (
            <Fragment key={k}>{field({ k, span, type: k === "quote_date" ? "date" : undefined })}</Fragment>
          ))}
        </div>

        {/* ── 价格信息 ────────────────────────────────────────────────── */}
        {sectionTitle(t("pq.sectionPrice"))}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-x-3 gap-y-1.5">
          {COST_FIELDS.filter(isFieldVisible).map((k) => <Fragment key={k}>{field({ k, type: "number" })}</Fragment>)}
          {/* 2026-06 · 其他 — read-only sum of 其他明细 when details exist. */}
          {isFieldVisible("others") && (
          <div className="space-y-1 col-span-2">
            <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
              {t("pq.others")}
              {hasOtherDetails && <span className="text-[9px] text-brand">{t("pq.othersLockedHint")}</span>}
            </Label>
            <div className="flex items-center gap-1">
              <Input type="number" step="0.01" {...inputProps("others")}
                readOnly={hasOtherDetails}
                className={`h-8 text-sm ${hasOtherDetails ? "bg-secondary/60 text-muted-foreground" : ""}`} />
              <Button type="button" variant="outline" size="sm"
                className="h-8 px-2 shrink-0 border-brand/50 text-brand hover:bg-brand/10 whitespace-nowrap"
                onClick={() => setOtherDetailsOpen(true)} data-testid="pq-other-details-button"
                title={t("pq.otherDetails")}>
                <ListPlus size={14} />
                <span className="text-[11px]">{t("pq.otherDetails")}</span>
                {hasOtherDetails && <span className="font-mono text-[10px]">({form.others_details.length})</span>}
              </Button>
            </div>
          </div>
          )}
          {isFieldVisible("profit_tax") && field({ k: "profit_tax", type: "number" })}
          {/* 价格 — read-only formula result (2026-06 user spec). */}
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">{t("pq.price")}</Label>
            <div
              className="h-8 px-2 rounded-md border border-brand/40 bg-brand/5 flex items-center justify-end font-mono text-sm font-bold text-brand"
              data-testid="new-pq-price-computed"
            >
              {computedPrice ? computedPrice.toFixed(4).replace(/0+$/, "").replace(/\.$/, "") : "0"}
            </div>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground font-mono">{t("pq.priceFormulaHint")}</p>

        <OtherCostDetailsDialog
          open={otherDetailsOpen}
          onOpenChange={setOtherDetailsOpen}
          rows={form.others_details || []}
          onChange={(rows) => setField("others_details", rows)}
        />

        {/* ── 装箱数据 ────────────────────────────────────────────────── */}
        {sectionTitle(t("pq.sectionCarton"))}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-x-3 gap-y-1.5 items-end">
          {field({ k: "packing_rate" })}
          {CARTON_FIELDS.filter((k) => CM_FIELDS.has(k)).map((k) => (
            <Fragment key={k}>{field({ k, type: "number", suffix: "cm" })}</Fragment>
          ))}
          {field({ k: "qty_hq", type: "number", hint: t("pq.qtyHqAuto") })}
          {field({ k: "gross_weight", type: "number", suffix: "kg" })}
        </div>

        {/* 2026-06 · 订单产品资料 (合同订单一览表) — auto-matched by 产品编号,
            one click pulls its carton data into the fields above. */}
        <OrderProductAttachPanel itemNumber={form.item_number} onPick={applyOrderPacking} currentPrice={pqNum(form.price)} />

        {/* ── 备注 ───────────────────────────────────────────────────── */}
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">{t("pq.remark")}</Label>
          <Textarea rows={2} {...inputProps("remark")} className="text-sm min-h-[52px]" />
        </div>

        <div className="border-t border-border pt-3 mt-2">
          <p className="text-[11px] text-muted-foreground" data-testid="new-pq-source-hint">
            {t("pq.sourceHint")}
          </p>
        </div>

        <DialogFooter>
          {/* 2026-06-28 · 自动保存状态 */}
          {isEditing && (
            <span className="mr-auto self-center text-[11px] text-muted-foreground" data-testid="new-pq-autosave-state">
              {autoState === "saving" ? t("pq.autoSaving") : autoState === "saved" ? t("pq.savedApp") : autoState === "error" ? t("pq.draftRetained") : t("pq.autoSaveHint")}
              <span className="block"><PqSyncStatus record={syncRecord} poll={open} prefix="pq-editor-sync" /></span>
            </span>
          )}
          <Button variant="outline" onClick={() => leaveAfterSave(() => onOpenChange(false))} className="rounded-md" disabled={saving} data-testid="new-pq-cancel">
            {t("common.cancel")}
          </Button>
          {/* 2026-06 · 复制 — save the current values as a brand-new record. */}
          <Button variant="outline" onClick={() => handleSubmit(true)} disabled={saving}
            data-testid="new-pq-copy" className="rounded-md">
            <CopyPlus size={14} /> {t("pq.saveAsCopy")}
          </Button>
          <Button onClick={() => handleSubmit(false)} disabled={saving} data-testid="new-pq-submit" className="rounded-md bg-brand hover:bg-brand-hover">
            {saving ? "..." : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// snake_case -> camelCase, e.g. "item_number" -> "itemNumber"; matches translations// like pq.itemNumber, pq.nameCn, pq.priceMeter (but priceMeter already handled: price_per_meter -> pricePerMeter).
function camelKey(k) {
  return k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

// -----------------------------------------------------------------------------
// Product picker — debounced search over /api/products, click a row to fill.
// Fetches up to 20 matches per query; empty query returns most-recent products.
// -----------------------------------------------------------------------------
function ProductPickerDialog({ open, onOpenChange, onPick }) {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounced search: 300ms after the user stops typing (or the picker opens).
  // Empty query returns the most-recent products (backend already sorts by created_at desc).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products", {
          params: { page: 1, page_size: 20, ...(query ? { search: query } : {}) },
        });
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col p-0" data-testid="pq-product-picker">
        <div className="p-4 border-b border-border">
          <DialogTitle className="flex items-center gap-2 mb-3">
            <PackageSearch size={16} className="text-brand" /> {t("pq.pickFromProducts")}
          </DialogTitle>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("pq.pickSearchPlaceholder")}
              data-testid="pq-product-picker-search"
              className="pl-8 h-9"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="pq-product-picker-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 min-h-[300px]" data-testid="pq-product-picker-list">
          {loading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 size={18} className="animate-spin" />
            </div>
          )}
          {!loading && items.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-10">{t("pq.pickEmpty")}</p>
          )}
          {!loading && items.map((p) => (
            <button
              key={p._id || p.id || p.item_number}
              type="button"
              onClick={() => onPick(p)}
              data-testid={`pq-product-picker-row-${p.item_number}`}
              className="w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-md hover:bg-secondary/60 transition-colors"
            >
              <div className="w-10 h-10 rounded bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                {p.photo_url ? (
                  <img src={`${API.replace("/api", "")}${p.photo_url}`} alt="" className="w-full h-full object-contain" />
                ) : (
                  <PackageSearch size={16} className="text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm font-semibold truncate">{p.item_number}</div>
                <p className="text-xs text-muted-foreground line-clamp-1">
                  {p.description_zh || p.description || "-"}
                </p>
              </div>
              <span className="text-[10px] font-mono text-muted-foreground uppercase shrink-0">
                {p.category || ""}
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
